import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, TrendingUp, Play, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { gamesApi, Game } from '../services/api';

interface OyunData {
  id: string;
  name: string;
  imageUrl: string;
  provider: string;
  trend: 'up' | 'down' | 'stable';
}

const KonusulanOyunlar: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [oyunlar, setOyunlar] = useState<OyunData[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchOyunlar = async () => {
      try {
        const result = await gamesApi.list({ limit: 5 });
        if (result.success && result.data?.games) {
          const mappedGames: OyunData[] = result.data.games.map((game: Game) => ({
            id: game.id,
            name: game.name,
            imageUrl: game.thumbnailUrl || '',
            provider: game.provider,
            trend: 'up' as const,
          }));
          setOyunlar(mappedGames);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Oyunlar yüklenemedi:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOyunlar();
  }, []);

  // Henüz oyun verisi yoksa "Çok Yakında" göster
  if (!loading && (error || oyunlar.length === 0)) {
    return (
      <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">En Çok Konuşulan Oyunlar</h2>
          </div>
          <Link to="/slotlar" className="text-primary-400 hover:text-primary-300 text-sm">
            Tümünü Gör →
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Çok Yakında!</h3>
            <p className="text-gray-400 text-sm">
              En çok konuşulan oyunlar burada yer alacak.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">En Çok Konuşulan Oyunlar</h2>
        </div>
        <Link to="/slotlar" className="text-primary-400 hover:text-primary-300 text-sm">
          Tümünü Gör →
        </Link>
      </div>

      <div className="space-y-2 flex-1">
        {loading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 bg-dark-700/50 rounded-lg" />
            ))}
          </div>
        ) : (
          oyunlar.map((oyun, index) => (
            <motion.div
              key={oyun.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group flex items-center justify-between p-3 bg-dark-700/30 rounded-lg hover:bg-dark-700/50 transition-colors cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <div className="relative">
                  {oyun.imageUrl ? (
                    <img
                      src={oyun.imageUrl}
                      alt={oyun.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-dark-900/50 rounded-lg">
                    <Play className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <p className="text-white font-medium">{oyun.name}</p>
                  <p className="text-gray-400 text-sm">{oyun.provider}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className={`w-4 h-4 ${
                  oyun.trend === 'up' ? 'text-green-400' :
                  oyun.trend === 'down' ? 'text-red-400' : 'text-gray-400'
                }`} />
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default KonusulanOyunlar;
